import type {
  LayoutImportGeometry,
  LayoutImportPayload,
  LayoutImportPoint,
  LayoutImportZone,
  LayoutImportZoneKind,
} from '../../types/layout.types';
import type { BuildLayoutImportPayload } from './types';

// Twin of the agent's services/layout_import.py: keep the two in step.
const NAME_MAX_LENGTH = 120;
const SUMMARY_MAX_LENGTH = 500;
const ZONE_KINDS: readonly LayoutImportZoneKind[] = [
  'functional',
  'mep',
  'circulation',
  'exterior',
];
const DEFAULT_ZONE_KIND: LayoutImportZoneKind = 'functional';
// Point-count limits of the Monco DTOs: a ring for rooms and walls, a two-point segment for openings.
const RING_POINT_LIMITS: readonly [number, number] = [3, 1000];
const SEGMENT_POINT_LIMITS: readonly [number, number] = [2, 2];

const text = (value: unknown, maxLength: number): string =>
  typeof value === 'string' ? value.trim().slice(0, maxLength) : '';

// The room number, then its name ("B.2.7 Klassenzimmer"); the export keeps the number out of the names.
const joined = (number: string, name: string): string =>
  [number, name]
    .filter((part) => part !== '')
    .join(' ')
    .slice(0, NAME_MAX_LENGTH);

const positiveNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;

const isZoneKind = (value: unknown): value is LayoutImportZoneKind =>
  typeof value === 'string' &&
  (ZONE_KINDS as readonly string[]).includes(value);

// `[[x, y], ...]` as `[{x, y}, ...]`; empty when any vertex is not a pair of numbers.
const toPoints = (ring: unknown): LayoutImportPoint[] => {
  if (!Array.isArray(ring)) return [];
  const points: LayoutImportPoint[] = [];
  for (const pair of ring) {
    if (!Array.isArray(pair) || pair.length !== 2) return [];
    const [x, y] = pair as unknown[];
    if (typeof x !== 'number' || typeof y !== 'number') return [];
    points.push({ x, y });
  }
  return points;
};

const isWithin = (
  points: LayoutImportPoint[],
  [minimum, maximum]: readonly [number, number],
): boolean => points.length >= minimum && points.length <= maximum;

const toGeometry = (
  items: unknown,
  key: string,
  limits: readonly [number, number],
  skipped: Record<string, number>,
  collection: string,
): LayoutImportGeometry[] => {
  const kept: LayoutImportGeometry[] = [];
  for (const item of Array.isArray(items) ? items : []) {
    const points = toPoints((item as Record<string, unknown> | null)?.[key]);
    if (!isWithin(points, limits)) {
      skipped[collection] = (skipped[collection] ?? 0) + 1;
      continue;
    }
    kept.push({ points });
  }
  return kept;
};

export const buildLayoutImportPayload: BuildLayoutImportPayload = ({
  layoutExport,
  floorPlanId,
  pageIndex,
}) => {
  if (layoutExport.status !== 'ok') {
    throw new Error(
      `layout status is "${String(layoutExport.status)}", not "ok"`,
    );
  }
  const skipped: Record<string, number> = {};

  const zones: LayoutImportZone[] = [];
  const zoneIndexByExportId = new Map<string, number>();
  for (const zone of layoutExport.zones ?? []) {
    zoneIndexByExportId.set(zone.id, zones.length);
    zones.push({
      name: text(zone.name, NAME_MAX_LENGTH) || `Zone ${zones.length + 1}`,
      nameDe: text(zone.name_de, NAME_MAX_LENGTH) || null,
      kind: isZoneKind(zone.kind) ? zone.kind : DEFAULT_ZONE_KIND,
      summary: text(zone.summary, SUMMARY_MAX_LENGTH),
      summaryDe: text(zone.summary_de, SUMMARY_MAX_LENGTH) || null,
    });
  }

  const rooms = (layoutExport.rooms ?? []).flatMap((room, index) => {
    const points = toPoints(room.polygon);
    if (!isWithin(points, RING_POINT_LIMITS)) {
      skipped.rooms = (skipped.rooms ?? 0) + 1;
      return [];
    }
    const zoneIndex =
      room.zone_id === null ? undefined : zoneIndexByExportId.get(room.zone_id);
    const number = text(room.number, NAME_MAX_LENGTH);
    const nameEn = text(room.name_en, NAME_MAX_LENGTH);
    const nameDe = text(room.name_de, NAME_MAX_LENGTH);
    return [
      {
        // The room number first: users name rooms by the architect's number (A.0.5).
        name: (nameEn ? joined(number, nameEn) : number) || `Room ${index + 1}`,
        nameDe: nameDe ? joined(number, nameDe) : null,
        points,
        zoneIndex: zoneIndex ?? null,
        statedAreaM2: positiveNumber(room.printed_area_m2),
      },
    ];
  });

  const payload: LayoutImportPayload = {
    floorPlanId,
    pageIndex,
    zones,
    rooms,
    walls: toGeometry(layoutExport.walls, 'outline', RING_POINT_LIMITS, skipped, 'walls'),
    doors: toGeometry(layoutExport.doors, 'segment', SEGMENT_POINT_LIMITS, skipped, 'doors'),
    windows: toGeometry(layoutExport.windows, 'segment', SEGMENT_POINT_LIMITS, skipped, 'windows'),
  };
  // Only a verified scale may calibrate the page; a provisional one would mislead every measurement.
  const metersPerPdfPoint = positiveNumber(layoutExport.units?.m_per_unit);
  if (layoutExport.units?.scale_verified === true && metersPerPdfPoint !== null) {
    payload.metersPerPdfPoint = metersPerPdfPoint;
  }
  return { payload, skipped };
};
